import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, Typography, Grid, Paper, CircularProgress, Alert } from '@mui/material';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { PayPalProvider } from './providers/PayPalProvider';
import Layout from './components/Layout';
import Cart from './components/Cart';
import ProductList from './components/ProductList';
import ProductFilter from './components/ProductFilter';
import UserProfile from './components/UserProfile';
import Notification from './components/Notification';
import Loading from './components/Loading';
import HeroSection from './components/HeroSection';
import { Product, CartItem, User, Purchase } from './types';
import EmailValidationDemo from './components/EmailValidationDemo';
import LoginDialog from './components/LoginDialog';
// import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import Favorites from './components/Favorites';
import UserDownloads from './components/UserDownloads';
import ProductListing from './components/ProductListing';
import AdminUpload from './components/AdminUpload';
import { userService } from './services/userService';

// Enhanced demo data
const demoProducts: Product[] = [
  {
    id: 'big-bulls',
    name: 'Big Bulls',
    description: 'Black and white line art illustration of a bull head with large curving horns. Minimalist design perfect for branding, logos, and graphic design projects.',
    price: 5.99,
    image: 'https://artful-aws.s3.us-east-2.amazonaws.com/Big_Bulls_Lines_All.svg',
    category: 'illustrations',
    rating: 4.8,
    downloads: 342,
    tags: ['bulls', 'line-art', 'minimalist', 'black-white', 'illustration'],
    author: 'ArtFul Design Team',
    fileSize: '45.2 KB',
    format: 'SVG',
    featured: true
  },
  {
    id: 'kid-goku',
    name: 'Kid Goku',
    description: 'Black and white line art illustration of Kid Goku flying on the Flying Nimbus cloud. Dynamic pose with characteristic spiky hair and tail. Perfect for anime fans and design projects.',
    price: 6.99,
    image: 'https://artful-aws.s3.us-east-2.amazonaws.com/Kid_Goku.svg',
    category: 'illustrations',
    rating: 4.9,
    downloads: 521,
    tags: ['goku', 'dragon-ball', 'anime', 'line-art', 'illustration'],
    author: 'ArtFul Design Team',
    fileSize: '38.7 KB',
    format: 'SVG',
    featured: true
  },
  {
    id: 'artful-logo',
    name: 'ArtFul Brand Logo',
    description: 'High-quality ArtFul brand logo in JPG format. Perfect for branding and marketing materials. Professional design suitable for business cards, websites, and promotional materials.',
    price: 9.99,
    image: 'https://artful-aws.s3.us-east-2.amazonaws.com/artFul.jpg',
    category: 'images',
    rating: 4.9,
    downloads: 125,
    tags: ['branding', 'logo', 'artful', 'professional', 'business'],
    author: 'ArtFul Design Team',
    fileSize: '293.89 KB',
    format: 'JPG',
    featured: true
  }
];

const demoUser: User = {
  id: 'demo-user-1',
  name: 'John Doe',
  email: 'john@example.com',
  joinDate: '2024-01-15',
  totalPurchases: 8,
  totalSpent: 189.95
};

// Demo existing emails for testing duplicate validation (will be replaced by real API call)
const existingEmails: string[] = [];

const demoPurchases: Purchase[] = [];

// Page Components
interface HomeProps {
  products: Product[];
  onRequireLogin?: () => void;
}

const Home: React.FC<HomeProps> = ({ products, onRequireLogin }) => {
  return (
    <Box>
      <HeroSection />
    </Box>
  );
};

const About = () => (
  <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
    <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ 
      fontSize: { xs: '2rem', md: '3rem' },
      mb: { xs: 3, md: 4 }
    }}>
      About ArtFul
    </Typography>
    
    <Typography variant="h5" gutterBottom sx={{ 
      mt: 4,
      fontSize: { xs: '1.3rem', md: '1.5rem' }
    }}>
      Your Premier SVG Marketplace
    </Typography>
    
    <Typography variant="body1" paragraph sx={{ 
      fontSize: { xs: '0.9rem', md: '1rem' },
      mb: { xs: 3, md: 4 },
      lineHeight: 1.6
    }}>
      ArtFul is the leading marketplace for high-quality SVG designs, serving designers, developers, and creative professionals worldwide. 
      We curate the finest collection of scalable vector graphics that empower your creative projects.
    </Typography>
    
    <Typography variant="h6" gutterBottom sx={{ 
      mt: { xs: 4, md: 5 },
      mb: { xs: 2, md: 3 },
      fontSize: { xs: '1.1rem', md: '1.25rem' }
    }}>
      Why Choose ArtFul?
    </Typography>
    
    <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 3, md: 4 } }}>
      <Grid item xs={12} sm={6} md={4}>
        <Paper elevation={2} sx={{ 
          p: { xs: 3, md: 4 }, 
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ 
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 2
          }}>
            Quality Assured
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            lineHeight: 1.5
          }}>
            Every design is carefully reviewed and tested to ensure the highest quality standards.
          </Typography>
        </Paper>
      </Grid>
      
      <Grid item xs={12} sm={6} md={4}>
        <Paper elevation={2} sx={{ 
          p: { xs: 3, md: 4 }, 
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ 
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 2
          }}>
            Instant Downloads
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            lineHeight: 1.5
          }}>
            Get your files immediately after purchase with our secure download system.
          </Typography>
        </Paper>
      </Grid>
      
      <Grid item xs={12} sm={6} md={4}>
        <Paper elevation={2} sx={{ 
          p: { xs: 3, md: 4 }, 
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ 
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 2
          }}>
            Expert Support
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: { xs: '0.8rem', md: '0.875rem' },
            lineHeight: 1.5
          }}>
            Our team of design experts is here to help you with any questions or issues.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  </Container>
);

interface ProductsProps {
  onAddToCart: (product: Product) => void;
  products: Product[];
  productsLoading: boolean;
  onRequireLogin?: () => void;
  onFavoriteAdded?: () => void;
}

const Products: React.FC<ProductsProps> = ({ onAddToCart, products, productsLoading, onRequireLogin, onFavoriteAdded }) => {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(false);

  // Update filtered products when products prop changes
  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  const categories = ['icons', 'logos', 'illustrations', 'graphics'];

  const handleSearch = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      let filtered = products;
      
      if (searchTerm) {
        filtered = filtered.filter(product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      if (selectedCategory) {
        filtered = filtered.filter(product => product.category === selectedCategory);
      }
      
      // Sort products
      switch (sortBy) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'rating':
          filtered.sort((a, b) => b.rating - a.rating);
          break;
        case 'popularity':
          filtered.sort((a, b) => b.downloads - a.downloads);
          break;
        default:
          // newest - keep original order
          break;
      }
      
      setFilteredProducts(filtered);
      setLoading(false);
    }, 500);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('newest');
    setFilteredProducts(products);
  };

  const handleProductDownload = async (product: Product) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        console.error('No auth token - please log in');
        return;
      }

      // Generate download URL from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/generate/${product.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Download error:', error);
        return;
      }

      const data = await response.json();
      
      // Use the blob approach for consistent file downloads
      if (data.downloadUrl) {
        try {
          const response = await fetch(data.downloadUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = data.fileName || `${product.name}.svg`;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
          console.log('Downloading:', product.name, `- ${data.downloadsRemaining || 0} downloads remaining`);
        } catch (error) {
          console.error('Error downloading file:', error);
        }
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };


  return (
    <Box>
      {/* Browse Products Heading - REMOVED */}
      
      {/* Add spacing between header and filter section */}
      <Box sx={{ pt: { xs: 4, md: 6 } }}>
        <ProductFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sortBy={sortBy}
          setSortBy={setSortBy}
          categories={categories}
          onSearch={handleSearch}
          onClearFilters={handleClearFilters}
        />
      </Box>
      
      {loading ? (
        <Loading message="Searching products..." />
      ) : (
        <ProductList
          products={filteredProducts}
          onAddToCart={onAddToCart}
          onDownload={handleProductDownload}
          onRequireLogin={onRequireLogin}
          onFavoriteAdded={onFavoriteAdded}
        />
      )}
    </Box>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [downloads, setDownloads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Transform orders to purchases format
  const transformOrdersToPurchases = useCallback((orders: any[]): Purchase[] => {
    return orders.map(order => {
      // Get first product name (or combine if multiple items)
      const firstItem = order.items?.[0];
      const productName = firstItem?.productId?.name || 
                         (typeof firstItem?.productId === 'string' ? 'Product' : 'Product');
      
      // Map status
      let purchaseStatus: 'completed' | 'pending' | 'failed' = 'pending';
      if (order.status === 'completed' && order.paymentStatus === 'paid') {
        purchaseStatus = 'completed';
      } else if (order.status === 'cancelled' || order.paymentStatus === 'refunded') {
        purchaseStatus = 'failed';
      }

      // Format date
      const orderDate = order.createdAt || order.created_at || new Date().toISOString();
      const formattedDate = new Date(orderDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      return {
        id: order._id || order.id,
        productName: order.items?.length > 1 
          ? `${productName} and ${order.items.length - 1} more`
          : productName,
        price: order.totalAmount || 0,
        date: formattedDate,
        status: purchaseStatus,
        productId: typeof firstItem?.productId === 'object' 
          ? (firstItem.productId._id || firstItem.productId.id || '')
          : (firstItem?.productId || '')
      };
    });
  }, []);

  // Fetch user data, orders, and downloads
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          setError('Please sign in to view your profile');
          setLoading(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const headers = { 'Authorization': `Bearer ${authToken}` };

        // Fetch user info
        try {
          const userResponse = await fetch(`${apiUrl}/auth/me`, { headers });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            const userInfo = userData.user || userData;
            setUser(userInfo);

            // Fetch orders (if user is available)
            try {
              const ordersResponse = await fetch(
                `${apiUrl}/orders/user/${userInfo.id || userInfo._id}`,
                { headers }
              );

              if (ordersResponse.ok) {
                const ordersData = await ordersResponse.json();
                // Transform orders to purchases
                const transformedPurchases = transformOrdersToPurchases(
                  ordersData.data?.orders || ordersData.orders || []
                );
                setPurchases(transformedPurchases);
              } else if (ordersResponse.status === 404) {
                // Orders endpoint might not exist yet, set empty array
                setPurchases([]);
              }
            } catch (ordersError) {
              console.error('Error fetching orders:', ordersError);
              // Don't fail entire profile load if orders fail
              setPurchases([]);
            }
          }
        } catch (userError) {
          console.error('Error fetching user:', userError);
          // Continue to try downloads even if user fetch fails
        }

        // Fetch downloads
        try {
          const downloadsResponse = await fetch(`${apiUrl}/downloads/user`, { headers });
          if (downloadsResponse.ok) {
            const downloadsData = await downloadsResponse.json();
            setDownloads(downloadsData.downloads || []);
          } else {
            setDownloads([]);
          }
        } catch (downloadsError) {
          console.error('Error fetching downloads:', downloadsError);
          setDownloads([]);
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [transformOrdersToPurchases]);

  const handleViewPurchases = () => {
    console.log('View purchases clicked');
    // TODO: Navigate to full purchases page when implemented
  };

  const handleViewDownloads = () => {
    navigate('/downloads');
  };

  if (loading) {
    return (
      <Box sx={{ pt: { xs: 4, md: 6 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: { xs: 4, md: 6 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: { xs: 4, md: 6 } }}>
      <UserProfile
        user={{
          name: user?.name || 'User',
          email: user?.email || '',
          joinDate: user?.joinDate || ((user as any)?.createdAt) || new Date().toISOString()
        }}
        purchases={purchases}
        downloads={downloads.length}
        onViewPurchases={handleViewPurchases}
        onViewDownloads={handleViewDownloads}
      />
    </Box>
  );
};

// Wrapper component to use navigation hook
const AppContent = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartBadgeCount, setCartBadgeCount] = useState<number>(0);
  const [notification, setNotification] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>(demoProducts);
  const [productsLoading, setProductsLoading] = useState(true);
  const [userDownloads, setUserDownloads] = useState<any[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Fetch products from backend on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        // Add cache-busting parameter to ensure fresh data
        const cacheBuster = `?_nocache=${Date.now()}`;
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/products${cacheBuster}`);
        const data = await response.json();
        
        if (data.success && data.data.products && data.data.products.length > 0) {
          console.log('✅ Loaded real products from backend:', data.data.products.length);
          setProducts(data.data.products);
        } else {
          console.log('⚠️ Using demo products - backend returned no products');
        }
      } catch (error) {
        console.error('❌ Failed to load products from backend, using demo data:', error);
        // Keep using demo products on error
      } finally {
        setProductsLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const handleAddToCart = (product: Product) => {
    // Check if user is authenticated before adding to cart
    if (!userService.isAuthenticated()) {
      requireLoginForCart();
      return;
    }

    setCartItems(prev => {
      // Check if item already exists in cart
      const existingItem = prev.find(item => item.id === product.id);
      
      let newItems;
      if (existingItem) {
        // Update quantity if item exists
        newItems = prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item to cart
        newItems = [...prev, {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity: 1,
          productId: product.id
        }];
      }
      
      // Update badge count
      const newBadgeCount = newItems.reduce((total, item) => total + item.quantity, 0);
      setCartBadgeCount(newBadgeCount);
      
      return newItems;
    });
    
    setNotification({
      id: Date.now().toString(),
      type: 'success',
      message: `${product.name} added to cart`
    });
  };

  const handleRemoveItem = (itemId: string) => {
    setCartItems(prev => {
      const newItems = prev.filter(item => item.id !== itemId);
      const newBadgeCount = newItems.reduce((total, item) => total + item.quantity, 0);
      setCartBadgeCount(newBadgeCount);
      return newItems;
    });
    setNotification({
      id: Date.now().toString(),
      type: 'success',
      message: 'Item removed from cart'
    });
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    
    setCartItems(prev => {
      const newItems = prev.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      );
      const newBadgeCount = newItems.reduce((total, item) => total + item.quantity, 0);
      setCartBadgeCount(newBadgeCount);
      return newItems;
    });
  };

  const handleClearCart = () => {
    setCartItems([]);
    setCartBadgeCount(0);
    setNotification({
      id: Date.now().toString(),
      type: 'info',
      message: 'Cart cleared'
    });
  };

  const handleClearCartBadge = () => {
    setCartBadgeCount(0);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLogin = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store tokens
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        
        const userRole = data.data.user.role || 'user';
        setUser({
          id: data.data.user.id,
          name: data.data.user.name,
          email: data.data.user.email,
          role: userRole
        });
        console.log('✅ User logged in', { userId: data.data.user.id, role: userRole });
        setLoginDialogOpen(false);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Login failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (email: string, password: string, name: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store tokens
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        
        const userRole = data.data.user.role || 'user';
        setUser({
          id: data.data.user.id,
          name: data.data.user.name,
          email: data.data.user.email,
          role: userRole
        });
        console.log('✅ User registered and logged in', { userId: data.data.user.id, role: userRole });
        setLoginDialogOpen(false);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setNotification({
      id: Date.now().toString(),
      type: 'success',
      message: 'Logged out successfully'
    });
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  // Centralized "please sign in first" flow for gated actions like favorites
  const requireLoginForFavorites = useCallback(() => {
    setNotification({
      message: 'Before you can do that... Sign in or register with your email address',
      severity: 'info'
    });
    handleLoginClick();
  }, []);

  // Centralized "please sign in first" flow for cart/purchase actions
  const requireLoginForCart = useCallback(() => {
    setNotification({
      message: 'Please sign in to add items to your cart and make purchases',
      severity: 'info'
    });
    handleLoginClick();
  }, []);

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleFavoritesClick = () => {
    navigate('/favorites');
  };

  const handleDownloadsClick = () => {
    navigate('/downloads');
  };

  // Fetch user downloads
  const fetchUserDownloads = useCallback(async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      // Verification logging
      console.log('🔍 App: Starting fetchUserDownloads');
      console.log('🔍 App: Auth token exists:', !!authToken);
      console.log('🔍 App: User state:', user);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/user`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      console.log('🔍 App: API Response status:', response.status);
      console.log('🔍 App: API Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 App: API Response data:', data);
        console.log('🔍 App: Downloads array:', data.downloads);
        console.log('🔍 App: Setting userDownloads to:', data.downloads || []);
        
        setUserDownloads(data.downloads || []);
      } else {
        const errorData = await response.json();
        console.error('🔍 App: API Error response:', errorData);
      }
    } catch (error) {
      console.error('🔍 App: Network/Other error:', error);
    }
  }, [user]);

  // Initialize authentication on app mount
  useEffect(() => {
    const initializeAuth = async () => {
      const authToken = localStorage.getItem('authToken');
      
      if (authToken) {
        try {
          // Validate token with backend
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/validate`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            // Restore user state
            setUser({
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              role: data.user.role || 'user' // Ensure role is set, default to 'user'
            });
            console.log('✅ User automatically logged in from stored token', { 
              userId: data.user.id, 
              role: data.user.role 
            });
          } else {
            // Token invalid, clear it
            localStorage.removeItem('authToken');
            console.log('❌ Stored token invalid, cleared');
          }
        } catch (error) {
          console.error('Error validating stored token:', error);
          localStorage.removeItem('authToken');
        }
      }
    };
    
    initializeAuth();
  }, []); // Run once on app mount

  // Fetch downloads when user is authenticated
  useEffect(() => {
    if (user) {
      fetchUserDownloads();
    }
  }, [user, fetchUserDownloads]);

  const handleDownload = async (productId: string) => {
    try {
      setDownloading(productId);
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setNotification({
          message: 'Please log in to download products',
          severity: 'error'
        });
        handleLoginClick();
        return;
      }

      // First, generate a download URL from the backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/downloads/generate/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate download link');
      }

      const data = await response.json();
      
      // Use the blob approach for consistent file downloads
      if (data.downloadUrl) {
        try {
          const response = await fetch(data.downloadUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = data.fileName || `product-${productId}`;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
          
          setNotification({
            message: `Download started! ${data.downloadsRemaining || 0} downloads remaining`,
            severity: 'success'
          });
        } catch (error) {
          console.error('Error downloading file:', error);
          setNotification({
            message: 'Download failed. Please try again.',
            severity: 'error'
          });
        }
      }
    } catch (error: any) {
      console.error('Download error:', error);
      setNotification({
        message: error.message || 'Failed to download product',
        severity: 'error'
      });
    } finally {
      setDownloading(null);
    }
  };

  // Wrapper for ProductListing component that takes Product instead of productId
  const handleProductDownload = async (product: Product) => {
    await handleDownload(product.id);
  };

  // Use badge count instead of calculating from cart items
  const cartItemCount = cartBadgeCount;

  return (
    <div className="App">
      <Layout
        downloadCount={userDownloads?.length || 0}
        isAuthenticated={!!user}
        onLoginClick={handleLoginClick}
        onLogout={handleLogout}
        userName={user?.name || ''}
        onLogoClick={handleLogoClick}
        cartItemCount={cartItemCount}
        onCartClick={handleCartClick}
        onFavoritesClick={handleFavoritesClick}
        onDownloadsClick={handleDownloadsClick}
        user={user}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 1, md: 2 }, px: { xs: 1, md: 2 } }}>
          <Routes>
            <Route path="/" element={<Home products={products} onRequireLogin={requireLoginForFavorites} />} />
            <Route path="/about" element={<About />} />
            <Route 
              path="/products" 
              element={
                <Products 
                  onAddToCart={handleAddToCart}
                  products={products}
                  productsLoading={productsLoading}
                  onRequireLogin={requireLoginForCart}
                  onFavoriteAdded={() => navigate('/favorites')}
                />
              } 
            />
            <Route 
              path="/listing/:productId" 
              element={
                <ProductListing
                  onAddToCart={handleAddToCart}
                  onDownload={handleProductDownload}
                  onRequireLogin={requireLoginForCart}
                  user={user}
                />
              } 
            />
            <Route path="/profile" element={<Profile />} />
            <Route path="/email-demo" element={<EmailValidationDemo />} />
            <Route 
              path="/cart" 
              element={
                <Cart
                  items={cartItems}
                  onRemoveItem={handleRemoveItem}
                  onUpdateQuantity={handleUpdateQuantity}
                  onClearCart={handleClearCart}
                  onClearCartBadge={handleClearCartBadge}
                />
              } 
            />
            <Route 
              path="/favorites" 
              element={
                <Favorites
                  onAddToCart={handleAddToCart}
                  onDownload={handleDownload}
                />
              } 
            />
            <Route 
              path="/downloads" 
              element={
                <UserDownloads
                  userSvgs={userDownloads || []}
                  onDownload={handleDownload}
                  downloading={downloading}
                  onRequireLogin={requireLoginForFavorites}
                />
              } 
            />
            <Route 
              path="/admin/upload" 
              element={
                <AdminUpload 
                  user={user}
                  onRequireLogin={handleLoginClick}
                />
              } 
            />
          </Routes>
        </Container>
      </Layout>

      <Notification
        notification={notification}
        onClose={handleCloseNotification}
      />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        onLogin={handleLogin}
        onRegister={handleRegister}
        loading={loading}
        error={error}
        existingEmails={existingEmails}
      />
    </div>
  );
};

function App() {
  return (
    <PayPalProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </PayPalProvider>
  );
}

export default App;
